import type { ReactNode } from "react";
import type { SourceCompareReport } from "../../game/types.js";

export interface CompareDetailsDrawerProps {
  compare: SourceCompareReport;
  /** Extra sections (constraints, share, etc.). */
  children?: ReactNode;
  defaultOpen?: boolean;
  testId?: string;
}

/**
 * Collapsed “What changed?” drawer for detailed edit lists.
 * Scoring pills stay outside — this only holds the long report.
 */
export function CompareDetailsDrawer({
  compare: c,
  children,
  defaultOpen = false,
  testId = "compare-details-drawer",
}: CompareDetailsDrawerProps) {
  return (
    <details
      className="compare-details-drawer flow-drawer"
      data-testid={testId}
      open={defaultOpen || undefined}
    >
      <summary data-testid="compare-details-summary">What changed?</summary>
      <div className="compare-details-body">
        <section className="compare-block">
          <h3>Beats</h3>
          <div className="compare-columns">
            <div>
              <h4>Kept</h4>
              <ul>
                {c.retainedBeats.map((b) => (
                  <li key={b.id}>{b.label}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4>Left out</h4>
              <ul>
                {c.omittedBeats.length === 0 && <li className="muted">None</li>}
                {c.omittedBeats.map((b) => (
                  <li key={b.id}>{b.label}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {c.frameExcludedCarriers.length > 0 && (
          <section className="compare-block">
            <h3>Outside the frame</h3>
            <ul>
              {c.frameExcludedCarriers.map((f) => (
                <li key={`${f.beatId}-${f.entityId}`}>
                  {f.entityLabel} during {f.beatLabel}
                </li>
              ))}
            </ul>
          </section>
        )}

        {c.captionClaims.length > 0 && (
          <section className="compare-block">
            <h3>Headline vs beats</h3>
            <ul>
              {c.captionClaims.map((cl) => (
                <li key={cl.claimId}>
                  {cl.text} <span className="tag">{cl.status}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {c.soundPriming.length > 0 && (
          <section className="compare-block">
            <h3>Sound</h3>
            <ul>
              {c.soundPriming.map((s) => (
                <li key={`${s.assetId}-${s.role}`}>
                  {s.role}: {s.note}
                </li>
              ))}
            </ul>
          </section>
        )}

        {c.cast.length > 0 && (
          <section className="compare-block">
            <h3>Casting</h3>
            <ul>
              {c.cast.map((row) => (
                <li key={row.slotId}>
                  {row.variantLabel}
                  {row.sourceFaithful ? " · source match" : " · reconstruction"}
                  {row.disclosureShown ? " · label on" : " · label off"}
                </li>
              ))}
            </ul>
          </section>
        )}

        {children}
      </div>
    </details>
  );
}
